
class BulkActionsCapability {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;
  }

  async handleBulkActions(data) {
    console.log("📦 BulkActionsCapability: Universal bulk actions started:", data);

    const { actions: bulkActions, programIndex, stage } = data;
    const results = [];
    let successCount = 0;
    let failureCount = 0;
    let hasStateChanges = false;

    // Validate input
    if (!bulkActions || !Array.isArray(bulkActions) || bulkActions.length === 0) {
      console.error("❌ Invalid bulk actions data:", data);
      return { success: false, message: "Invalid bulk actions data provided" };
    }

    console.log(`📦 Processing ${bulkActions.length} bulk actions for Program[${programIndex}].${stage}`);

    // Group actions by type for optimization
    const actionsByType = this.groupActionsByType(bulkActions);

    // Process actions in optimal order (removals first, then additions, then modifications)
    const executionOrder = ['remove_tools_from_stages', 'add_tools_to_stages', 'tool_duration_editing'];

    for (const actionType of executionOrder) {
      if (actionsByType[actionType]) {
        console.log(`📦 Processing ${actionsByType[actionType].length} ${actionType} actions`);

        for (const action of actionsByType[actionType]) {
          try {
            const result = await this.executeIndividualAction(action, programIndex, stage);

            results.push({
              type: actionType,
              success: result.success,
              message: result.message,
              data: action.data
            });

            if (result.success) {
              successCount++;
              hasStateChanges = true;
            } else {
              failureCount++;
            }

          } catch (error) {
            console.error(`❌ Failed to execute ${actionType}:`, error);
            results.push({
              type: actionType,
              success: false,
              message: `Error: ${error.message}`,
              data: action.data
            });
            failureCount++;
          }
        }
      }
    }

    // Process any remaining action types not in the predefined order
    for (const [actionType, actions] of Object.entries(actionsByType)) {
      if (!executionOrder.includes(actionType)) {
        console.log(`📦 Processing ${actions.length} ${actionType} actions`);

        for (const action of actions) {
          try {
            const result = await this.executeIndividualAction(action, programIndex, stage);

            results.push({
              type: actionType,
              success: result.success,
              message: result.message,
              data: action.data
            });

            if (result.success) {
              successCount++;
              hasStateChanges = true;
            } else {
              failureCount++;
            }

          } catch (error) {
            console.error(`❌ Failed to execute ${actionType}:`, error);
            results.push({
              type: actionType,
              success: false,
              message: `Error: ${error.message}`,
              data: action.data
            });
            failureCount++;
          }
        }
      }
    }

    // Save once after all changes if any succeeded
    if (hasStateChanges) {
      console.log("📦 Triggering save after bulk actions");
      this.setters.setIsSaved(false);

      setTimeout(() => {
        if (this.savePlan && typeof this.savePlan === 'function') {
          this.savePlan();
        }
      }, 1000);
    }

    // Create detailed result message
    let detailedMessage;
    if (failureCount === 0) {
      detailedMessage = `Bulk operation completed successfully: ${successCount} actions executed`;
    } else if (successCount === 0) {
      detailedMessage = `Bulk operation failed: ${failureCount} actions failed`;
    } else {
      detailedMessage = `Bulk operation partially completed: ${successCount} successful, ${failureCount} failed`;
    }

    if (results.length > 0) {
      const successfulActions = results.filter(r => r.success).map(r => r.type);
      const failedActions = results.filter(r => !r.success).map(r => `${r.type} (${r.message})`);

      if (successfulActions.length > 0) {
        detailedMessage += `\n✅ Successful: ${successfulActions.join(', ')}`;
      }
      if (failedActions.length > 0) {
        detailedMessage += `\n❌ Failed: ${failedActions.join(', ')}`;
      }
    }

    const summary = {
      success: successCount > 0,
      message: detailedMessage,
      totalActions: bulkActions.length,
      successCount,
      failureCount,
      results,
      partialSuccess: successCount > 0 && failureCount > 0,
      hasStateChanges
    };

    console.log(`📦 ✅ Universal bulk actions complete:`, summary);
    return summary;
  }

  groupActionsByType(bulkActions) {
    const grouped = {};

    bulkActions.forEach(action => {
      const type = action.type;
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(action);
    });

    return grouped;
  }

  async executeIndividualAction(action, defaultProgramIndex, defaultStage) {
    // FIX #50: Add defensive null checks to prevent "Cannot read properties of null" errors
    if (!action) {
      console.error("❌ executeIndividualAction: action is null or undefined");
      return { success: false, message: "Invalid action: action is null" };
    }

    const { type: actionType, data: actionData } = action;

    // FIX #50: Handle null/undefined actionData gracefully
    if (!actionData || typeof actionData !== 'object') {
      console.error("❌ executeIndividualAction: actionData is null or not an object", { actionType, actionData });
      return { success: false, message: `Invalid action data for ${actionType}: data is missing or invalid` };
    }

    // Normalize data to include program/stage context if missing
    const normalizedData = {
      ...actionData,
      programIndex: actionData.programIndex ?? defaultProgramIndex,
      stage: actionData.stage ?? defaultStage
    };

    console.log(`📦 Executing individual action: ${actionType}`, normalizedData);

    // Execute based on action type
    switch (actionType) {
      case 'tool_duration_editing':
        return await this.executeToolDurationEdit(normalizedData);

      case 'add_tools_to_stages':
        return await this.executeAddTools(normalizedData);

      case 'remove_tools_from_stages':
        return await this.executeRemoveTools(normalizedData);

      default:
        console.warn(`📦 Unknown action type: ${actionType}`);
        return { 
          success: false, 
          message: `Unknown action type: ${actionType}` 
        };
    }
  }

  async executeToolDurationEdit(data) {
    const { 
      programIndex: targetProgramIndex, 
      stage: targetStage, 
      tool: targetToolName,
      toolName: alternateToolName,
      newDuration: updatedDuration,
      duration: alternateDuration
    } = data;

    const actualToolName = targetToolName || alternateToolName;
    const actualDuration = updatedDuration || alternateDuration;

    // Validate input data
    if (targetProgramIndex === undefined || !targetStage || !actualToolName || actualDuration === undefined) {
      console.error("❌ Invalid tool duration editing data:", data);
      return { success: false, message: "Invalid data: missing programIndex, stage, toolName, or duration" };
    }

    console.log(`📦🔧 Updating ${actualToolName} duration to ${actualDuration} hours in Program[${targetProgramIndex}].${targetStage}`);

    // Capture initial state for validation
    const initialSequence = this.getters.finalProgramSequence || [];
    if (targetProgramIndex >= initialSequence.length) {
      return { success: false, message: `Program index ${targetProgramIndex} out of bounds` };
    }

    const initialProgram = initialSequence[targetProgramIndex];
    if (!initialProgram?.toolsByStage?.[targetStage]?.tools) {
      return { success: false, message: `Stage '${targetStage}' not found in program` };
    }

    const initialTool = initialProgram.toolsByStage[targetStage].tools.find(t => t.tool === actualToolName);
    if (!initialTool) {
      return { success: false, message: `Tool '${actualToolName}' not found in stage '${targetStage}'` };
    }

    const initialDuration = initialTool.duration;

    // Helper function to update program tools duration
    const updateProgramToolDuration = (program) => {
      if (!program?.toolsByStage?.[targetStage]?.tools) {
        console.warn(`📦🔧 Stage ${targetStage} not found in program ${program?.program_of_work}`);
        return program;
      }

      const toolIndex = program.toolsByStage[targetStage].tools.findIndex(t => t.tool === actualToolName);
      if (toolIndex === -1) {
        console.warn(`📦🔧 Tool ${actualToolName} not found in stage ${targetStage}`);
        return program;
      }

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      updatedProgram.toolsByStage = { ...program.toolsByStage };
      updatedProgram.toolsByStage[targetStage] = { ...program.toolsByStage[targetStage] };
      updatedProgram.toolsByStage[targetStage].tools = [...program.toolsByStage[targetStage].tools];

      // Update the specific tool duration
      updatedProgram.toolsByStage[targetStage].tools[toolIndex] = {
        ...updatedProgram.toolsByStage[targetStage].tools[toolIndex],
        duration: actualDuration
      };

      // Recalculate stage total duration
      updatedProgram.toolsByStage[targetStage].totalDuration = 
        updatedProgram.toolsByStage[targetStage].tools.reduce((sum, tool) => sum + (tool.duration || 0), 0);

      // Recalculate program total duration
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage)
        .reduce((sum, stageData) => sum + (stageData.totalDuration || 0), 0);

      return updatedProgram;
    };

    try {
      // Update finalProgramSequence
      this.setters.setFinalProgramSequence(prevSequence => {
        const updatedSequence = [...prevSequence];
        updatedSequence[targetProgramIndex] = updateProgramToolDuration(updatedSequence[targetProgramIndex]);

        // Calculate and update total duration
        const newTotalDuration = updatedSequence.reduce((sum, program) => 
          sum + (program.totalProgramLength || 0), 0
        );
        this.setters.setTotalDuration(newTotalDuration);

        return updatedSequence;
      });

      // Update selectedPrograms
      this.setters.setSelectedPrograms(prevSelected => {
        const updatedSelected = [...prevSelected];
        if (updatedSelected[targetProgramIndex]) {
          updatedSelected[targetProgramIndex] = updateProgramToolDuration(updatedSelected[targetProgramIndex]);
        }
        return updatedSelected;
      });

      return {
        success: true,
        message: `${actualToolName} duration updated from ${initialDuration} to ${actualDuration} hours`
      };

    } catch (error) {
      console.error("❌ Error updating tool duration:", error);
      return {
        success: false,
        message: `Failed to update tool duration: ${error.message}`
      };
    }
  }

  async executeAddTools(data) {
    const { programIndex: addProgIdx, stage: addStage, tools: toolsToAdd } = data;

    // Validate input data
    if (addProgIdx === undefined || !addStage || !toolsToAdd || !Array.isArray(toolsToAdd) || toolsToAdd.length === 0) {
      console.error("❌ Invalid tool addition data:", data);
      return { success: false, message: "Invalid data: missing programIndex, stage, or tools array" };
    }

    console.log(`📦➕ Adding ${toolsToAdd.length} tools to Program[${addProgIdx}].${addStage}`);

    // Validate program exists
    const initialSequence = this.getters.finalProgramSequence || [];
    if (addProgIdx >= initialSequence.length) {
      return { success: false, message: `Program index ${addProgIdx} out of bounds` };
    }

    const initialProgram = initialSequence[addProgIdx];
    if (!initialProgram) {
      return { success: false, message: `Program at index ${addProgIdx} not found` };
    }

    // Capture initial state for validation
    const initialToolsCount = initialProgram.toolsByStage?.[addStage]?.tools?.length || 0;

    // Helper function to add tools to a program
    const addToolsToProgram = (program) => {
      if (!program) {
        console.warn(`📦➕ Program at index ${addProgIdx} not found`);
        return program;
      }

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      updatedProgram.toolsByStage = { ...program.toolsByStage };

      // Initialize toolsByStage if it doesn't exist
      if (!updatedProgram.toolsByStage) {
        updatedProgram.toolsByStage = {};
      }

      // Initialize stage if it doesn't exist
      if (!updatedProgram.toolsByStage[addStage]) {
        updatedProgram.toolsByStage[addStage] = { tools: [], totalDuration: 0 };
      }

      updatedProgram.toolsByStage[addStage] = { ...updatedProgram.toolsByStage[addStage] };

      // Add new tools
      const newTools = toolsToAdd.map((tool, index) => ({
        id: `tool-${Date.now()}-${index}`,
        tool: tool.tool || tool.name,
        description: tool.description || `${tool.tool || tool.name} tool description`,
        duration: tool.duration || 1,
        related_stage: addStage,
        complexity: tool.complexity || "medium"
      }));

      updatedProgram.toolsByStage[addStage].tools = [...updatedProgram.toolsByStage[addStage].tools, ...newTools];

      // Recalculate stage total duration
      updatedProgram.toolsByStage[addStage].totalDuration = updatedProgram.toolsByStage[addStage].tools.reduce(
        (sum, t) => sum + (t.duration || 0), 0
      );

      // Recalculate program total duration
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage).reduce(
        (sum, stageData) => sum + (stageData.totalDuration || 0), 0
      );

      return updatedProgram;
    };

    try {
      // Update finalProgramSequence
      this.setters.setFinalProgramSequence(prevSequence => {
        const updatedSequence = [...prevSequence];
        updatedSequence[addProgIdx] = addToolsToProgram(updatedSequence[addProgIdx]);

        // Calculate and update total duration
        const newTotalDuration = updatedSequence.reduce((sum, program) => 
          sum + (program.totalProgramLength || 0), 0
        );
        this.setters.setTotalDuration(newTotalDuration);

        return updatedSequence;
      });

      // Update selectedPrograms
      this.setters.setSelectedPrograms(prevSelected => {
        const updatedSelected = [...prevSelected];
        if (updatedSelected[addProgIdx]) {
          updatedSelected[addProgIdx] = addToolsToProgram(updatedSelected[addProgIdx]);
        }
        return updatedSelected;
      });

      return {
        success: true,
        message: `Successfully added ${toolsToAdd.length} tools to ${addStage} stage (was ${initialToolsCount}, now ${initialToolsCount + toolsToAdd.length})`
      };

    } catch (error) {
      console.error("❌ Error adding tools:", error);
      return {
        success: false,
        message: `Failed to add tools: ${error.message}`
      };
    }
  }

  async executeRemoveTools(data) {
    const { programIndex: progIdx, stage: stageToModify, tools: toolsToRemove } = data;

    // Validate input data
    if (progIdx === undefined || !stageToModify || !toolsToRemove || !Array.isArray(toolsToRemove) || toolsToRemove.length === 0) {
      console.error("❌ Invalid tool removal data:", data);
      return { success: false, message: "Invalid data: missing programIndex, stage, or tools array" };
    }

    console.log(`📦🗑️ Removing tools [${toolsToRemove.join(', ')}] from Program[${progIdx}].${stageToModify}`);

    // Validate program exists
    const initialSequence = this.getters.finalProgramSequence || [];
    if (progIdx >= initialSequence.length) {
      return { success: false, message: `Program index ${progIdx} out of bounds` };
    }

    const initialProgram = initialSequence[progIdx];
    if (!initialProgram?.toolsByStage?.[stageToModify]?.tools) {
      return { success: false, message: `Stage '${stageToModify}' not found in program or has no tools` };
    }

    // Capture initial state for validation
    const initialToolsCount = initialProgram.toolsByStage[stageToModify].tools.length;

    // Helper function to remove tools from a program
    const removeToolsFromProgram = (program) => {
      if (!program?.toolsByStage?.[stageToModify]?.tools) {
        console.warn(`📦🗑️ Stage ${stageToModify} not found in program ${program?.program_of_work}`);
        return program;
      }

      // Create deep copy to avoid mutations
      const updatedProgram = { ...program };
      updatedProgram.toolsByStage = { ...program.toolsByStage };
      updatedProgram.toolsByStage[stageToModify] = { ...program.toolsByStage[stageToModify] };

      // Remove specified tools
      const initialTools = updatedProgram.toolsByStage[stageToModify].tools;
      updatedProgram.toolsByStage[stageToModify].tools = initialTools.filter(
        tool => {
          const shouldRemove = toolsToRemove.some(toolToRemove => {
            if (typeof toolToRemove === 'string') {
              return tool.tool === toolToRemove || tool.name === toolToRemove;
            } else if (typeof toolToRemove === 'object') {
              const toolName = toolToRemove.tool || toolToRemove.name;
              return tool.tool === toolName || tool.name === toolName;
            }
            return false;
          });
          return !shouldRemove;
        }
      );

      // Recalculate stage total duration
      updatedProgram.toolsByStage[stageToModify].totalDuration = updatedProgram.toolsByStage[stageToModify].tools.reduce(
        (sum, t) => sum + (t.duration || 0), 0
      );

      // Recalculate program total duration
      updatedProgram.totalProgramLength = Object.values(updatedProgram.toolsByStage).reduce(
        (sum, stageData) => sum + (stageData.totalDuration || 0), 0
      );

      return updatedProgram;
    };

    try {
      // Update finalProgramSequence
      this.setters.setFinalProgramSequence(prevSequence => {
        const updatedSequence = [...prevSequence];
        updatedSequence[progIdx] = removeToolsFromProgram(updatedSequence[progIdx]);

        // Calculate and update total duration
        const newTotalDuration = updatedSequence.reduce((sum, program) => 
          sum + (program.totalProgramLength || 0), 0
        );
        this.setters.setTotalDuration(newTotalDuration);

        return updatedSequence;
      });

      // Update selectedPrograms
      this.setters.setSelectedPrograms(prevSelected => {
        const updatedSelected = [...prevSelected];
        if (updatedSelected[progIdx]) {
          updatedSelected[progIdx] = removeToolsFromProgram(updatedSelected[progIdx]);
        }
        return updatedSelected;
      });

      const expectedFinalCount = initialToolsCount - toolsToRemove.length;
      
      return {
        success: true,
        message: `Successfully removed ${toolsToRemove.length} tools from ${stageToModify} stage (was ${initialToolsCount}, now ${expectedFinalCount})`
      };

    } catch (error) {
      console.error("❌ Error removing tools:", error);
      return {
        success: false,
        message: `Failed to remove tools: ${error.message}`
      };
    }
  }
}

module.exports = BulkActionsCapability;
